import type { ColDef, ICellRendererParams } from 'ag-grid-community';
import { formatDate, formatRelativeTime } from '@utils/format';
import { ActiveBadge } from '@features/tags/components/cells/ActiveBadge';
import { ApmcCell } from './components/cells/ApmcCell';
import { FlagCell } from './components/cells/FlagCell';
import { NotesCell } from './components/cells/NotesCell';
import { PriceSeriesRowActions } from './components/cells/PriceSeriesRowActions';
import type { PriceSeries } from './schema';

export interface PriceSeriesRowActionHandlers {
  onEdit: (row: PriceSeries) => void;
}

// `colId` on sortable columns matches the API's sortBy key.
export function buildPriceSeriesColumns(
  actions: PriceSeriesRowActionHandlers,
  canEdit: boolean,
): ColDef<PriceSeries>[] {
  const columns: ColDef<PriceSeries>[] = [
    {
      colId: 'apmc',
      headerName: 'APMC',
      flex: 1.5,
      minWidth: 200,
      sortable: true,
      pinned: 'left',
      lockPinned: true,
      cellRenderer: ApmcCell,
      // Comparator-by-name only matters for the visible page; server sort is
      // authoritative across pages.
      valueGetter: (p) => p.data?.apmcName ?? '',
    },
    {
      colId: 'crop',
      field: 'cropName',
      headerName: 'Crop',
      flex: 1,
      minWidth: 140,
      sortable: true,
    },
    {
      colId: 'variety',
      field: 'varietyName',
      headerName: 'Variety',
      flex: 1,
      minWidth: 140,
      sortable: true,
    },
    {
      field: 'isActive',
      headerName: 'Active',
      width: 110,
      // Reuse the ActiveBadge from tags — same tonal pill semantics.
      cellRenderer: ActiveBadge,
    },
    {
      field: 'isManualMarketPrice',
      headerName: 'Manual',
      width: 100,
      cellRendererParams: {
        onLabel:
          'Manual market price ON — a draft observation is pre-created each morning for field agents to fill.',
        offLabel: 'Manual market price OFF — prices come from scrapers only.',
      },
      cellRenderer: FlagCell,
    },
    {
      field: 'runPredictionPipeline',
      headerName: 'Forecast pipeline',
      width: 150,
      cellRendererParams: {
        onLabel:
          'Forecast pipeline ON — the prediction model runs for this series.',
        offLabel: 'Forecast pipeline OFF — no model runs; cost gate.',
      },
      cellRenderer: FlagCell,
    },
    {
      field: 'showPredictionInApp',
      headerName: 'Forecast shown',
      width: 140,
      cellRendererParams: {
        onLabel: 'Forecasts ON — visible in the consumer app.',
        offLabel:
          'Forecasts OFF — hidden in the app (independent of pipeline).',
      },
      cellRenderer: FlagCell,
    },
    {
      field: 'notes',
      headerName: 'Notes',
      flex: 2,
      minWidth: 180,
      cellRenderer: NotesCell,
    },
    {
      colId: 'updated',
      field: 'updatedAt',
      headerName: 'Updated',
      width: 200,
      sortable: true,
      cellRenderer: (p: ICellRendererParams<PriceSeries>) =>
        p.data ? (
          <div className="flex flex-col leading-tight">
            <span className="text-[13px] font-semibold text-foreground">
              {formatDate(p.data.updatedAt)}
            </span>
            <span className="mt-0.5 text-[11px] text-foreground-muted">
              {formatRelativeTime(p.data.updatedAt)}
            </span>
          </div>
        ) : null,
    },
  ];

  if (canEdit) {
    columns.push({
      headerName: 'Actions',
      width: 100,
      sortable: false,
      filter: false,
      pinned: 'right',
      cellRenderer: (p: ICellRendererParams<PriceSeries>) =>
        p.data ? <PriceSeriesRowActions row={p.data} {...actions} /> : null,
    });
  }

  return columns;
}
